import { clsx } from 'clsx'
import { Link } from 'react-router-dom'

import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { AskAiAvailability } from '@tribeplatform/gql-client/types'
import { useAskAIAccess, useAuthMember } from '@tribeplatform/react-sdk/hooks'
import { Badge } from '@tribeplatform/react-ui-kit/Badge'
import { Button, IconButton } from '@tribeplatform/react-ui-kit/Button'
import { SvgIcon } from '@tribeplatform/react-ui-kit/Icon'

import { COMMAND_BAR_HOTKEYS } from '../../../CommandBar/index.js'
import { useControlledHotkeys } from '../../../common/hooks/useControlledHotkeys.js'
import { T, useI18n } from '../../../i18n/index.js'
import { ResultsView } from '../../SmartSearch.types.js'
import { AiStarIcon } from '../AiStarIcon.js'

type Props = {
  text: string
  className?: string
  onSubmit?: () => void
  view?: ResultsView
  isHidden?: boolean
}

export const AskAIResultRow = ({
  text,
  className,
  onSubmit,
  view = 'command-bar',
  isHidden = false,
}: Props) => {
  const { isAdmin } = useAuthMember()

  const { $t } = useI18n()
  const label = text
    ? `${$t({
        id: 'SmartSearch.AskAI.AskAIPrefix',
        defaultMessage: 'Ask AI',
      })} `
    : $t({
        id: 'SmartSearch.AskAI.AskAIAbout',
        defaultMessage: 'Ask AI about...',
      })

  const quotedText = `"${text}"`

  const { canAskAi, askAiAvailability } = useAskAIAccess()

  useControlledHotkeys(
    COMMAND_BAR_HOTKEYS.ASK_AI.value,
    e => {
      if (canAskAi) {
        e.preventDefault()
        onSubmit()
      }
    },
    { enabled: true, enableOnFormTags: true },
  )

  if (!canAskAi) {
    return null
  }

  return (
    <div className={clsx(className, { hidden: isHidden }, 'p-2')}>
      <Button
        disabled={isHidden}
        onClick={() => onSubmit()}
        className="flex w-full select-none items-center rounded-md text-md font-normal text-content-subdued"
        variant="tertiaryNeutral"
        size="md"
      >
        <>
          <div className="flex w-full justify-between py-2 items-center group">
            <div className="flex gap-2 flex-1 items-center overflow-hidden">
              <AiStarIcon />

              <div className="flex flex-1 gap-1 whitespace-nowrap w-full overflow-hidden">
                <span>{label}</span>
                <span
                  className={clsx(
                    'flex-1 pr-1 truncate',
                    text ? 'flex' : 'hidden',
                  )}
                >
                  {quotedText}
                </span>
              </div>

              {askAiAvailability === AskAiAvailability.staffOnly && (
                <div className="flex gap-1 items-center">
                  <Badge
                    tooltip={
                      <div className="max-w-60">
                        <T
                          id="SmartSearch.AskAI.OnlyVisibleToAdminsAndModsTooltip"
                          defaultMessage="Only available to staff. Click the settings icon to manage access for your community members."
                        />
                      </div>
                    }
                    tooltipProps={{
                      offsetValue: 12,
                    }}
                    leadingIcon={<SvgIcon name="eye-off" />}
                    className={clsx(
                      'hidden md:flex items-center gap-1 rounded-full text-label-sm whitespace-nowrap group-hover:bg-surface cursor-default',
                      {
                        'py-0.5 px-2 md:text-label-sm': view === 'command-bar',
                        'py-1 px-3 md:text-label-md': view === 'search-page',
                      },
                    )}
                    onClick={e => e.stopPropagation()}
                  >
                    <T
                      id="SmartSearch.AskAI.OnlyVisibleToAdminsAndMods"
                      defaultMessage="Only available to staff"
                    />
                  </Badge>

                  {isAdmin && (
                    <IconButton
                      tabIndex={-1}
                      as={Link}
                      to={adminRoutes.settings.search.root}
                      rounded
                      tooltip
                      accessibilityLabel={$t({
                        id: 'Generics.Settings',
                        defaultMessage: 'Settings',
                      })}
                      tooltipProps={{
                        offsetValue: 12,
                      }}
                      size={view === 'command-bar' ? 'sm' : 'md'}
                      variant="plainNeutral"
                      className="group/icon-button"
                      icon={
                        <SvgIcon
                          name="sliders"
                          size={view === 'command-bar' ? 'md' : 'lg'}
                          className="text-content-subdued group-hover/icon-button:text-content"
                        />
                      }
                    />
                  )}
                </div>
              )}
              <div className="hidden lg:flex gap-1 items-center cursor-pointer hover:opacity-85 text-xs">
                <span className="h-6 px-2 flex items-center justify-center rounded-md bg-surface border [&>kbd]:text-xs">
                  {COMMAND_BAR_HOTKEYS.ASK_AI.label}
                </span>
              </div>
            </div>
          </div>
        </>
      </Button>
    </div>
  )
}
